import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { Loader2, Trash2, Check, Phone } from "lucide-react";

export const BookingsAdmin = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const mark = async (b, status) => {
    try {
      await api.patch(`/bookings/${b.id}?status=${status}`);
      load();
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete enquiry from ${b.name}?`)) return;
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Enquiry deleted.");
      load();
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-bookings-panel">
      <div className="mb-8">
        <p className="text-teal text-[10px] uppercase tracking-[0.3em] font-semibold">Enquiries</p>
        <h2 className="font-display text-3xl text-navy mt-1">
          Booking requests <span className="text-navy/40 text-2xl">({bookings.length})</span>
        </h2>
      </div>

      {loading ? (
        <Loader2 className="animate-spin text-teal" />
      ) : bookings.length === 0 ? (
        <p className="text-sm text-slate-500">No enquiries yet. Requests from the website form appear here.</p>
      ) : (
        <div className="space-y-4">
          {bookings.map((b, i) => (
            <div
              key={b.id}
              data-testid={`admin-booking-row-${i}`}
              className="bg-white border border-navy/10 p-6 grid lg:grid-cols-[1.2fr_1fr_1fr_auto] gap-5 items-start"
            >
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-display text-xl text-navy">{b.name}</h3>
                  <span
                    className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 ${
                      b.status === "called" ? "bg-teal/10 text-teal" : "bg-navy/5 text-navy/60"
                    }`}
                  >
                    {b.status === "called" ? "Called" : "New"}
                  </span>
                </div>
                <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-sm text-slate-600 mt-2 hover:text-teal transition-colors">
                  <Phone size={14} className="text-teal" /> {b.phone}
                </a>
                {b.email && <p className="text-xs text-slate-500 mt-1">{b.email}</p>}
              </div>
              <div className="text-sm text-slate-600">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Service</p>
                <p className="mt-1">{b.service_type}</p>
                {b.preferred_date && <p className="text-xs text-slate-500 mt-1">Preferred: {b.preferred_date}</p>}
              </div>
              <div className="text-sm text-slate-600">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Centre</p>
                <p className="mt-1">{b.centre}</p>
                {b.message && <p className="text-xs text-slate-500 mt-2 italic">“{b.message}”</p>}
              </div>
              <div className="flex gap-2">
                {b.status !== "called" && (
                  <button
                    onClick={() => mark(b, "called")}
                    data-testid={`admin-booking-mark-called-${i}`}
                    className="inline-flex items-center gap-2 border border-teal text-teal px-3 py-2 text-[11px] uppercase tracking-[0.15em] hover:bg-teal hover:text-white transition-colors"
                  >
                    <Check size={14} /> Called
                  </button>
                )}
                <button
                  onClick={() => remove(b)}
                  data-testid={`admin-booking-delete-${i}`}
                  className="border border-red-200 text-red-600 px-3 py-2 hover:bg-red-600 hover:text-white transition-colors"
                  aria-label="Delete enquiry"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
